<script setup>
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from 'vue';

// // Store management imports
import { useAwsStore } from '@/stores/aws';
import { useBreakStore } from '@/stores/break';
import { useGeneralStore } from '@/stores/general';
import { useTimeStore } from '@/stores/time';
import { useTrackerStore } from '@/stores/tracker';

// // Utils imports
import { CommunicationUtil } from '@samsung360vr/overlay-app-sdk';
import { logDebug, logError, logInfo, logWarn } from '@/utils/logger';

// // Module imports
import PollInput from './PollInput.vue';
import PollOutput from './PollOutput.vue';
import { PollHeroOptions } from './pollHeroUtils';

// // Composables
import { usePollResults } from '@/composables/usePollResults';

// // Component imports
import BarcodeQr from '@/components/BarcodeQr/BarcodeQrComp.vue';

// // Asset imports
import pollRemoteTopSilverBlackSrc from '@/assets/images/icons/instruction-s-remote-top-silver-black.png';
import { toggleVideoSound } from '@/utils/videoUtils';
import { BreakModeEnum } from '@/utils/dataAnalyzers';

const storeAws = useAwsStore();
const storeBreak = useBreakStore();
const storeGeneral = useGeneralStore();
const storeTime = useTimeStore();
const storeTracker = useTrackerStore();

const isKeyCurrentlyPressed = ref(false);
const hasOptionBeenSelected = ref(false);
const hasNavigationFrozen = ref(false);
const isReadyToShow = ref(false);
const hasSelectedOptionLclStrge = ref(false);
const hasBeenScannedSuccessfully = ref(false);
const activeIndex = ref(0);
const pressedKey = ref('');
const optionSelectedLclStrge = ref(-1);
const resetLSCounter = ref(0);
const countdownSeconds = ref(27);

// // To store the AWS credential provider (reactive)
const awsCredentialProvider = ref(null);
// const instructionsSeconds = ref(5);
const instructionsSeconds = ref(7);
// const instructionsSeconds = ref(55);
// const instructionsSeconds = ref(35);

const appInstance = import.meta.env.VITE_APP_ENV;

const { calculatedPollPercentages, getSelectionHandler, castVote } = usePollResults({
  optionKeys: Object.values(PollHeroOptions),
  getBreakUuid: () => storeBreak.breakUuid,
  getAwsCredentialProvider: () => awsCredentialProvider.value,
});

// Video element references for full mode only
const isMuted = ref(true); // Start muted by default to comply with autoplay policies
const videoRef = ref(null);

const toggleSound = () => {
  toggleVideoSound(videoRef, isMuted);
};

const props = defineProps({
  pollMainBackground: {
    type: String,
    default: 'rgba(234, 172, 195, 1)',
  },
  pollTaglineColor: {
    type: String,
    default: 'rgba(255, 255, 255, 1)',
  },
});

const styleObjectMain = reactive({
  background: `${props.pollMainBackground}`,
});

const styleObjectTagline = reactive({
  color: props.pollTaglineColor,
});

// // LowerHeroPoll handler functions

const keyDownHandler = (event) => {
  // console.log(`You pressed ${event.key}: ${event.code}.`);

  CommunicationUtil.onKeyPressed(event.keyCode);
  isKeyCurrentlyPressed.value = true;
  pressedKey.value = event.key;
};

const keyUpHandler = () => {
  // console.log(`You are no longer pressing a key.`);

  isKeyCurrentlyPressed.value = false;
  pressedKey.value = '';
};

const backgroundHandler = () => {
  return styleObjectMain;
};

const scanHandler = (e) => {
  hasBeenScannedSuccessfully.value = e;

  logDebug(`Event received from child: ${hasBeenScannedSuccessfully.value}`);
};

const updateLocalStoragePoll = () => {
  hasSelectedOptionLclStrge.value = true;
  localStorage.setItem(
    `${storeBreak.breakUuid}-lSHasSelectedOption`,
    hasSelectedOptionLclStrge.value,
  );
  optionSelectedLclStrge.value = activeIndex.value;
  localStorage.setItem(`${storeBreak.breakUuid}-lSOptionSelected`, optionSelectedLclStrge.value);
};

const buttonSelectHandler = async () => {
  // castVote handles the get-then-put fallback, the optimistic result, and
  // scheduling the graceful update + bounded retry — see usePollResults.
  await castVote(activeIndex.value, storeBreak.breakOptions[activeIndex.value].optionLabel);

  hasOptionBeenSelected.value = true;

  // // Update beacon with accept invitation event
  if (storeTracker.isIframe && ['dev', 'stage', 'prod'].includes(appInstance)) {
    if (storeTracker.trackingInfo) {
      if (
        storeTracker.trackingInfo.trackingEvents &&
        storeTracker.trackingInfo.trackingEvents.length > 0
      ) {
        storeTracker.trackerBeacon.beaconManager.sendTrackingEvent(
          storeTracker.trackingInfo,
          'acceptInvitation',
          storeBreak.breakUuid,
        );
      }
    }
  }

  updateLocalStoragePoll();
};

const countdownSecondsHandler = (durationInSecondsParam) => {
  setInterval(() => {
    if (durationInSecondsParam.value > 0) {
      durationInSecondsParam.value--;
    }
  }, 1000);
};

// // LowerHeroPoll functions

const countdownTimer = () => {
  setInterval(() => {
    if (countdownSeconds.value > 0) {
      countdownSeconds.value--;
    }
  }, 1000);
};

const shouldShowResults = computed(() => {
  // This computed property will automatically update when hasOptionBeenSelected changes.
  return hasOptionBeenSelected.value;
});

const selectedPollOption = computed(() => {
  // This computed property reactively returns the selected poll option.
  return Number(optionSelectedLclStrge.value);
});

const getPollColors = computed(() => {
  return {
    bar: 'rgba(124, 57, 151, 1)',
    resultsBox: 'rgba(52, 45, 73, 1)',
  };
});

// // Configure timer settings for this component (dynamic based on video duration)
const lowerHeroPollTimerConfig = computed(() => {
  // // Get video duration from break store, fallback to 30 seconds if not available
  const videoDuration = parseFloat(storeBreak.breakPlaybackVideoDuration) || 30;

  return {
    fadeInStart: 5.5, // // seconds - when to start fading in
    fadeInEnd: 7.5, // // seconds - when fade in completes
    fadeOutStart: videoDuration - 6, // // seconds - when to start fading out
    fadeOutEnd: videoDuration - 5, // // seconds - when fade out completes
  };
});

// // Configure app container settings for this component (different timing than timer)
const appContainerTimerConfig = computed(() => {
  // // Get video duration from break store, fallback to 30 seconds if not available
  const videoDuration = parseFloat(storeBreak.breakPlaybackVideoDuration) || 30;

  return {
    fadeInStart: 5.5, // // seconds - when to start fading in the app container
    fadeInEnd: 7.5, // // seconds - when fade in completes
    fadeOutStart: videoDuration - 3, // // seconds - when to start fading out
    fadeOutEnd: videoDuration - 1, // // seconds - when fade out completes
  };
});

// // Time-based computed properties for app container visibility
const shouldShowAppContainer = computed(() => {
  // // Get visibility state using app container config
  const visibility = storeTime.getTimerVisibility(appContainerTimerConfig.value);
  return visibility.shouldShow;
});

const handlePokeData = (dataParam) => {
  activeIndex.value = dataParam;
  buttonSelectHandler();
};

// // LowerHeroPoll lifecycle hooks

onBeforeMount(async () => {
  try {
    // // 1. Check for existing selection in localStorage
    const storedLocalSelectionFlag = localStorage.getItem(
      `${storeBreak.breakUuid}-lSHasSelectedOption`,
    );
    const storedLocalSelectionOption = localStorage.getItem(
      `${storeBreak.breakUuid}-lSOptionSelected`,
    );
    hasSelectedOptionLclStrge.value = storedLocalSelectionFlag === 'true';
    optionSelectedLclStrge.value = storedLocalSelectionOption
      ? Number(storedLocalSelectionOption)
      : -1;

    // // 0. Initialize AWS Cognito credentials and signer in the store
    const identityPoolId = import.meta.env.VITE_APP_IDENTITY_POOL_ID;

    if (identityPoolId) {
      // Initialize AWS credentials and signer
      if (storeAws.isInitialized.value && storeAws.awsCredentialProvider.value) {
        awsCredentialProvider.value = storeAws.awsCredentialProvider.value;
      } else {
        awsCredentialProvider.value = await storeAws.initializeAwsCredentials(
          identityPoolId,
          import.meta.env.VITE_APP_AWS_REGION || 'us-east-1',
        );
      }

      // Note: API calls will be made in the isBreakReady watcher to avoid race condition
    } else {
      logWarn('VITE_APP_IDENTITY_POOL_ID is not set. AWS calls may not be authenticated.');
    }
  } catch (error) {
    logError('Error during onBeforeMount in TempLowerHeroPoll:', error);
  }
});

// // IMPORTANT: This onMounted hook is now async.
// // The <Suspense> component in the parent will wait for this to complete.
onMounted(async () => {
  // // 1. Update timer configuration when component mounts
  // //    Apply custom timer configuration for this component
  storeTime.updateTimerConfig(lowerHeroPollTimerConfig.value);

  // // 2. Keep the original delay for consistency.
  // await new Promise((resolve) => setTimeout(resolve, 850));
  logDebug('Initial delay complete (commented out).');

  // // 3. Set up event listeners and start the timer.
  // //    This happens after fonts and delay.
  document.addEventListener('keydown', keyDownHandler);
  document.addEventListener('keyup', keyUpHandler);

  isReadyToShow.value = true;
  // // Initialize time manager for parent window communication
  // // Time store will automatically detect if spoof timer should be used
  try {
    // // Now that we know the Tizen status, start the appropriate timer
    if (
      !storeTracker.isIframe ||
      [BreakModeEnum.FULLMODE, BreakModeEnum.BROWSER].includes(storeBreak.breakDisplayMode)
    ) {
      logDebug('Using spoof timer (Tizen is false OR mode set to full/browser)');
      storeTime.startSpoofTimer(500); // // Update every 500ms to simulate real behavior
    } else {
      // // Use real parent window communication for production
      logDebug('Using real time manager (Tizen is true)');
      storeTime.initializeTimeManager();
    }

    // // Apply custom timer configuration for this component
    storeTime.updateTimerConfig(lowerHeroPollTimerConfig.value);
    countdownSecondsHandler(instructionsSeconds);
  } catch (error) {
    logError('Error initializing time manager:', error);
    // // Continue without time manager if it fails
    countdownTimer();
    countdownSecondsHandler(instructionsSeconds);
  }
});

onBeforeUnmount(() => {
  // // Clean up time manager
  storeTime.stopSpoofTimer();
  storeTime.deactivateTimeManager();
});

onUnmounted(() => {
  document.removeEventListener('keydown', keyDownHandler);
  document.removeEventListener('keyup', keyUpHandler);
});

// // LowerHeroPoll watchers

watch(isKeyCurrentlyPressed, (isKeyCurrentlyPressedNewVal) => {
  if (isKeyCurrentlyPressedNewVal) {
    // console.log('LowerHeroPoll – Key is pressed down');
    // console.log(`pressedKey.value: ${pressedKey.value}`);

    if (
      !hasOptionBeenSelected.value &&
      !hasNavigationFrozen.value &&
      instructionsSeconds.value === 0
    ) {
      if (pressedKey.value === 'ArrowRight') {
        if (activeIndex.value < storeBreak.breakOptions.length - 1) {
          activeIndex.value++;
        }
      } else if (pressedKey.value === 'ArrowLeft') {
        if (activeIndex.value > 0) {
          activeIndex.value--;
        }
      } else if (pressedKey.value === 'Enter') {
        hasNavigationFrozen.value = true;

        // logDebug(`Button Selected – optionLabel: ${props.pollOptions[activeIndex.value].optionLabel}`);
        logDebug(
          `Button Selected – optionLabel: ${storeBreak.breakOptions[activeIndex.value].optionLabel}`,
        );

        buttonSelectHandler();
      }
    } else {
      if (pressedKey.value === 'ArrowUp' && storeGeneral.instance.designation !== 'prod') {
        resetLSCounter.value++;
      }
    }
  }
});

watch(resetLSCounter, (resetLSCounterNewVal) => {
  if (resetLSCounterNewVal && resetLSCounterNewVal === 7) {
    logDebug('Clearing poll localStorage data');
    // Only clear poll-specific data
    localStorage.removeItem(`${storeBreak.breakUuid.value}-lSHasSelectedOption`);
    localStorage.removeItem(`${storeBreak.breakUuid.value}-lSOptionSelected`);
    // Reset the counter so it can be used again
    resetLSCounter.value = 0;
  }
});

watch(hasBeenScannedSuccessfully, () => {
  logInfo(`Scan success?: ${hasBeenScannedSuccessfully.value}`);
});

// Watch for isBreakReady to become true before making API calls
// This prevents the race condition where breakUuid is not yet available
watch(
  [() => storeBreak.isBreakReady, awsCredentialProvider],
  async ([isBreakReadyNewVal, credentialProvider]) => {
    if (isBreakReadyNewVal && credentialProvider) {
      logDebug('isBreakReady is true and credentials are ready, fetching poll data');
      // Determine if previous vote present, if so, call results and move on else call in background
      if (hasSelectedOptionLclStrge.value) {
        // User has already voted, fetch results and show PollOutput
        await getSelectionHandler('isBreakReadyWatcher').then(
          () => (hasOptionBeenSelected.value = true),
        );
      } else {
        // No vote yet, fetch initial results in background
        getSelectionHandler('isBreakReadyWatcher');
      }
    }
  },
);
</script>

<template>
  <section class="lower-hero-poll">
    <!-- Background video - Only show in full mode -->
    <video
      v-if="storeBreak.breakDisplayMode === 'full' && storeBreak.breakPlaybackVideoSrc"
      ref="videoRef"
      class="lower-hero-poll__background-video"
      :src="storeBreak.breakPlaybackVideoSrc"
      autoplay
      loop
      :muted="isMuted"
      playsinline
    ></video>

    <div
      v-if="storeBreak.breakDisplayMode === 'full' && storeBreak.breakPlaybackVideoSrc"
      class="lower-hero-poll__video-click-overlay"
      @click="toggleSound"
    ></div>
    <!-- End background video - Only show in full mode -->

    <div
      class="lower-hero-poll__container"
      :class="{
        'is-hidden': !isReadyToShow,
        'is-clear': !shouldShowAppContainer,
        'is-opaque': shouldShowAppContainer,
      }"
    >
      <!-- <div class="lower-hero-poll__scrim"></div> -->

      <aside class="lower-hero-poll__strip">
        <div
          v-for="item in storeBreak.breakProducts"
          :key="item.productPosition"
          class="lower-hero-poll__attention"
        >
          <div class="lower-hero-poll__tagline" :style="styleObjectTagline">
            <span class="lower-hero-poll__copy">{{ storeBreak.breakTaglineCopy }}</span>
          </div>

          <div class="lower-hero-poll__quick-response">
            <BarcodeQr
              class="lower-hero-poll__qr"
              :target-url="item.productUrl"
              :interactive-break-uuid="storeBreak.breakUuid"
              :qr-success="'em'"
              :product-uuid="item.productUuid"
              :ad-id="storeBreak.breakGeneratedIdentifier"
              @scan-success="scanHandler"
            />

            <div
              class="lower-hero-poll__underlay"
              :class="{
                'lower-hero-poll__underlay--pre': !hasBeenScannedSuccessfully,
                'lower-hero-poll__underlay--post': hasBeenScannedSuccessfully,
              }"
            ></div>
          </div>
        </div>
      </aside>

      <PollInput
        v-show="!shouldShowResults && instructionsSeconds === 0"
        :input-box-background="backgroundHandler()"
        :ad-headline="storeBreak.breakHeadlineCopy"
        :ad-headline-color="storeBreak.breakHeadlineColor"
        :poll-options="storeBreak.breakOptions"
        :focus-index="activeIndex"
        :break-uuid="storeBreak.breakUuid"
        @poll-input-emitted="handlePokeData"
      />
      <!-- v-show="!shouldShowResults" -->

      <div v-show="!shouldShowResults && instructionsSeconds > 0" class="poll-input">
        <div class="poll-input__container">
          <div class="lower-hero-poll__countdown">{{ instructionsSeconds }}</div>
          <div class="poll-input__wrapper" :style="backgroundHandler()">
            <div
              class="poll-input__tagline poll-input__tagline--instructions"
              :style="storeBreak.breakHeadlineColor"
            >
              <span class="poll-input__tagline-copy">Grab your remote to vote!</span>
            </div>
          </div>
        </div>

        <div class="lower-hero-poll__instructions">
          <img class="lower-hero-poll__remote-img" :src="pollRemoteTopSilverBlackSrc" />
        </div>
      </div>

      <PollOutput
        v-show="shouldShowResults"
        :output-box-background="backgroundHandler()"
        :ad-headline="storeBreak.breakHeadlineCopy"
        :ad-headline-color="storeBreak.breakHeadlineColor"
        :poll-options="storeBreak.breakOptions"
        :focus-index="selectedPollOption"
        :break-uuid="storeBreak.breakUuid"
        :poll-percentages="calculatedPollPercentages"
        :bar-color="getPollColors.bar"
        :results-box-background-color="getPollColors.resultsBox"
      />
    </div>
  </section>
</template>
